import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  HiXMark,
  HiClock,
  HiTrophy,
  HiPencilSquare,
  HiLink,
  HiChevronLeft,
} from "react-icons/hi2";
import { PeakEvent, addDaysYMD, eventLastDay } from "../../types/event";
import { Session, TrainingLog } from "../../types/post";
import { useOverlayStore } from "../../stores/overlayStore";
import { getAllPerformanceTrainingLogs } from "../../services/dayPostService";
import {
  EventPerformance,
  getLinkedSessions,
  getPerformancesForEvent,
} from "../../services/eventService";
import { linkPerformanceToEvent } from "../../services/performanceService";
import PerformanceIntentBadge from "../shared/PerformanceIntentBadge";
import EventTagChips from "../shared/EventTagChips";
import { formatResultVariable } from "../../utils/performanceFormat";
import {
  countdownLabel,
  formatDateRange,
  formatDisplayDate,
} from "../../utils/goalDates";
import { EventVM, logDateForEvent } from "./eventVM";

export const EVENT_SHEET_Z_INDEX_CLASS = "z-[900]";

/** Link picker window: performances within the event range ± this many days. */
const LINK_WINDOW_DAYS = 3;

export interface EventBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  event: EventVM | null;
  /** Owner of the event (for the linked-data queries). */
  ownerUserId?: string;
  /** Opens the Performance flow pre-seeded with this event. */
  onLogPerformance: (event: PeakEvent) => void;
  /** Opens the edit sheet (athlete-authored events only). */
  onEdit: (event: PeakEvent) => void;
  /** Called after a link / unlink so the Schedule can refresh "Logged". */
  onLinksChanged?: () => void;
}

/**
 * Bottom sheet for an event tapped on the schedule (Events.md §4): name, type
 * badge, dates, tags, notes, linked sessions, linked
 * performances (load + results) and Log performance / Link existing
 * performance / Edit. Coach-issued events are read-only.
 */
const EventBottomSheet: React.FC<EventBottomSheetProps> = ({
  isOpen,
  onClose,
  event: vm,
  ownerUserId,
  onLogPerformance,
  onEdit,
  onLinksChanged,
}) => {
  const [performances, setPerformances] = useState<EventPerformance[]>([]);
  const [sessions, setSessions] = useState<(Session & { id: string })[]>([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [candidates, setCandidates] = useState<(TrainingLog & { id: string })[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const registerBottomSheet = useOverlayStore((s) => s.registerBottomSheet);
  const unregisterBottomSheet = useOverlayStore((s) => s.unregisterBottomSheet);
  useEffect(() => {
    if (!isOpen) return;
    registerBottomSheet();
    return () => unregisterBottomSheet();
  }, [isOpen, registerBottomSheet, unregisterBottomSheet]);

  const eventId = vm?.id;

  const load = useCallback(async () => {
    if (!eventId || !ownerUserId) {
      setPerformances([]);
      setSessions([]);
      return;
    }
    setLoading(true);
    try {
      const [perfs, linkedSessions] = await Promise.all([
        getPerformancesForEvent(ownerUserId, eventId),
        getLinkedSessions(ownerUserId, eventId),
      ]);
      setPerformances(perfs);
      setSessions(linkedSessions);
    } catch {
      setPerformances([]);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [eventId, ownerUserId]);

  useEffect(() => {
    if (!isOpen) {
      setPickerOpen(false);
      setShowAll(false);
      setError(null);
      return;
    }
    load();
  }, [isOpen, load]);

  const openPicker = async () => {
    if (!ownerUserId) return;
    setPickerOpen(true);
    setPickerLoading(true);
    setError(null);
    try {
      const logs = await getAllPerformanceTrainingLogs(ownerUserId);
      setCandidates(logs.filter((l) => !l.eventId));
    } catch {
      setCandidates([]);
    } finally {
      setPickerLoading(false);
    }
  };

  const handleLink = async (trainingLogId: string) => {
    if (!ownerUserId || !eventId) return;
    setBusyId(trainingLogId);
    setError(null);
    try {
      await linkPerformanceToEvent(ownerUserId, trainingLogId, eventId);
      setPickerOpen(false);
      await load();
      onLinksChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not link performance");
    } finally {
      setBusyId(null);
    }
  };

  const handleUnlink = async (trainingLogId: string) => {
    if (!ownerUserId) return;
    setBusyId(trainingLogId);
    setError(null);
    try {
      await linkPerformanceToEvent(ownerUserId, trainingLogId, null);
      await load();
      onLinksChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not unlink performance");
    } finally {
      setBusyId(null);
    }
  };

  const event = vm?.event;
  const logged = performances.length > 0;
  const withdrawn = event?.status === "withdrawn";
  const countdown = vm && !withdrawn ? countdownLabel(vm.date) : null;
  const readOnly = !!vm?.coachIssued;

  const windowStart = event ? addDaysYMD(event.date, -LINK_WINDOW_DAYS) : "";
  const windowEnd = event ? addDaysYMD(eventLastDay(event), LINK_WINDOW_DAYS) : "";
  const visibleCandidates = candidates.filter(
    (l) => showAll || (l.dateID >= windowStart && l.dateID <= windowEnd),
  );

  return (
    <AnimatePresence>
      {isOpen && vm && event && (
        <motion.div
          key="event-sheet-backdrop"
          className={`fixed inset-0 ${EVENT_SHEET_Z_INDEX_CLASS} bg-black/50 backdrop-blur-sm`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="event-sheet-title"
            className="fixed bottom-0 left-0 right-0 mx-auto w-full sm:max-w-md bg-base-100 rounded-t-2xl shadow-xl flex flex-col max-h-[88vh]"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-12 h-1 bg-base-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-start justify-between gap-3 px-5 pt-1 pb-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <PerformanceIntentBadge type={vm.type} />
                  {withdrawn ? (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide font-grotesk bg-base-200 text-base-content/60">
                      Withdrawn
                    </span>
                  ) : (
                    logged && (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide font-grotesk bg-success/15 text-success">
                        Logged
                      </span>
                    )
                  )}
                </div>
                <h2
                  id="event-sheet-title"
                  className="text-lg font-heading text-base-content truncate mt-0.5"
                >
                  {vm.name}
                </h2>
                <EventTagChips tags={event.tags} className="mt-1.5" />
                <div className="flex items-center gap-1.5 text-xs text-base-content/60 font-grotesk mt-1">
                  <HiClock className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="font-geist tabular-nums">
                    {formatDateRange(vm.date, vm.endDate)}
                    {countdown && <> · {countdown}</>}
                  </span>
                </div>
                {readOnly && (
                  <p className="text-xs text-base-content/60 font-grotesk mt-1.5">
                    Set by your coach
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="w-11 h-11 -mt-1.5 -mr-2 flex items-center justify-center rounded-full hover:bg-base-200 transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <HiXMark className="w-5 h-5 text-base-content/60" />
              </button>
            </div>

            {pickerOpen ? (
              <div className="px-5 pb-3 overflow-y-auto space-y-3 min-h-0">
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setPickerOpen(false)}
                    className="inline-flex items-center gap-1 text-sm text-base-content/70 hover:text-base-content font-grotesk min-h-11"
                  >
                    <HiChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                  <label className="flex items-center gap-2 text-xs font-grotesk text-base-content/70 min-h-11 cursor-pointer">
                    <input
                      type="checkbox"
                      className="toggle toggle-sm"
                      checked={showAll}
                      onChange={(e) => setShowAll(e.target.checked)}
                    />
                    Show all
                  </label>
                </div>
                <p className="text-[11px] uppercase tracking-wider font-grotesk text-base-content/50">
                  {showAll
                    ? "All unlinked performances"
                    : `Unlinked performances within ${LINK_WINDOW_DAYS} days of the event`}
                </p>
                {pickerLoading ? (
                  <div className="text-center py-4">
                    <span className="loading loading-spinner loading-sm" />
                  </div>
                ) : visibleCandidates.length === 0 ? (
                  <p className="text-sm text-base-content/60 font-grotesk py-2">
                    No unlinked performances{showAll ? "" : " in this range"}.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {visibleCandidates.map((log) => (
                      <button
                        key={log.id}
                        type="button"
                        disabled={busyId !== null}
                        onClick={() => handleLink(log.id)}
                        className="w-full text-left rounded-xl bg-base-200 border border-base-300 p-3 hover:border-base-content/40 transition-colors disabled:opacity-50"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <PerformanceIntentBadge type={log.performanceType} />
                            <span className="text-xs text-base-content/60 font-geist">
                              {formatDisplayDate(log.dateID, { day: "numeric", month: "short", year: "numeric" })}
                            </span>
                          </div>
                          <span className="text-xs text-base-content/70 font-geist tabular-nums">
                            Load {log.load} · RPE {log.rpe}
                          </span>
                        </div>
                        {log.notes?.trim() && (
                          <p className="mt-1 text-xs text-base-content/60 font-grotesk truncate">
                            {log.notes}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {error && <p className="text-sm text-error font-grotesk">{error}</p>}
              </div>
            ) : (
              <>
                {/* Scrollable content */}
                <div className="px-5 pb-3 overflow-y-auto space-y-4 min-h-0">
                  {vm.notes?.trim() && (
                    <p className="text-sm text-base-content/80 font-grotesk whitespace-pre-wrap">
                      {vm.notes}
                    </p>
                  )}

                  <div className="rounded-xl bg-base-200 border border-base-300 p-3">
                    <p className="text-[11px] uppercase tracking-wider font-grotesk text-base-content/50 mb-2">
                      Sessions
                      <span className="font-geist normal-case tracking-normal text-base-content/70 ml-1.5">
                        {sessions.length}
                      </span>
                    </p>
                    {sessions.length === 0 ? (
                      <p className="text-xs text-base-content/60 font-grotesk">
                        No sessions linked to this event.
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {sessions.map((s) => (
                          <div
                            key={s.id}
                            className="flex items-center justify-between gap-2 text-sm font-grotesk"
                          >
                            <span className="truncate">{s.name?.trim() || "Session"}</span>
                            <span className="text-xs text-base-content/60 font-geist tabular-nums flex-shrink-0">
                              {formatDisplayDate(s.plannedDate.replace(/-/g, ""), {
                                day: "numeric",
                                month: "short",
                              })}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl bg-base-200 border border-base-300 p-3">
                    <p className="text-[11px] uppercase tracking-wider font-grotesk text-base-content/50 mb-2">
                      Performances
                      <span className="font-geist normal-case tracking-normal text-base-content/70 ml-1.5">
                        {performances.length}
                      </span>
                    </p>
                    {loading ? (
                      <div className="text-center py-2">
                        <span className="loading loading-spinner loading-xs" />
                      </div>
                    ) : performances.length === 0 ? (
                      <p className="text-xs text-base-content/60 font-grotesk">
                        Nothing logged yet.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {performances.map(({ trainingLog: log, results }) => (
                          <div key={log.id} className="rounded-lg bg-base-100 border border-base-300 p-3">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                <PerformanceIntentBadge type={log.performanceType} />
                                <span className="text-xs text-base-content/60 font-geist">
                                  {formatDisplayDate(log.dateID, { day: "numeric", month: "short" })}
                                  {log.eventDay ? ` · Day ${log.eventDay}` : ""}
                                </span>
                              </div>
                              <span className="text-xs text-base-content/70 font-geist tabular-nums">
                                Load {log.load} · RPE {log.rpe}
                              </span>
                            </div>
                            {results.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {results.map((r) => {
                                  const measured = (r.activity?.variables ?? [])
                                    .filter((v) => v.measurement != null)
                                    .map(formatResultVariable)
                                    .join(" · ");
                                  return (
                                    <div
                                      key={r.id}
                                      className="flex items-center justify-between gap-2 text-xs font-grotesk"
                                    >
                                      <span className="truncate">
                                        <span className="font-medium">
                                          {r.activity?.activityName ?? "Result"}
                                        </span>
                                        {measured && (
                                          <span className="text-base-content/70 font-geist tabular-nums">
                                            : {measured}
                                          </span>
                                        )}
                                      </span>
                                      {(r.tag ?? [])
                                        .filter((t) => t === "PB" || t === "SB")
                                        .map((t) => (
                                          <span
                                            key={t}
                                            className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded flex-shrink-0 bg-success/15 text-success"
                                          >
                                            {t}
                                          </span>
                                        ))}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <button
                              type="button"
                              disabled={busyId !== null}
                              onClick={() => handleUnlink(log.id)}
                              className="mt-2 text-xs text-base-content/50 hover:text-base-content/80 font-grotesk disabled:opacity-50 min-h-9"
                            >
                              {busyId === log.id ? "Unlinking..." : "Unlink"}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {error && <p className="text-sm text-error font-grotesk">{error}</p>}
                </div>

                {/* Footer actions */}
                <div className="px-5 pt-2 pb-4 border-t border-base-200 space-y-2">
                  {!withdrawn && (
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => onLogPerformance(event)}
                        className="flex items-center justify-center gap-2 py-3 min-h-11 rounded-xl bg-primary text-primary-content font-grotesk font-semibold hover:bg-primary/90 transition-colors"
                      >
                        <HiTrophy className="w-4 h-4" />
                        {logged ? "Log another" : "Log performance"}
                      </button>
                      <button
                        type="button"
                        onClick={openPicker}
                        className="flex items-center justify-center gap-2 py-3 min-h-11 rounded-xl bg-base-200 text-base-content font-grotesk font-semibold hover:bg-base-300 transition-colors"
                      >
                        <HiLink className="w-4 h-4" />
                        Link existing
                      </button>
                    </div>
                  )}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => onEdit(event)}
                      className="w-full flex items-center justify-center gap-2 py-3 min-h-11 rounded-xl bg-base-200 text-base-content font-grotesk font-semibold hover:bg-base-300 transition-colors"
                    >
                      <HiPencilSquare className="w-4 h-4" />
                      Edit
                    </button>
                  )}
                  <p className="text-[11px] text-center text-base-content/50 font-grotesk">
                    {withdrawn ? (
                      readOnly ? (
                        "This event is off the calendar."
                      ) : (
                        "This event is off the calendar. Edit it to reinstate or delete it."
                      )
                    ) : (
                      <>
                        Logging files the performance under{" "}
                        {formatDisplayDate(logDateForEvent(event), {
                          day: "numeric",
                          month: "short",
                        })}
                        .
                      </>
                    )}
                  </p>
                </div>
              </>
            )}

            <div className="pb-safe" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EventBottomSheet;
